<template>
  <div v-if="showChartInfo">
    <v-button
      icon="times"
      style-type="link"
      size="xs"
      @click="showChartInfo = false"
    />
  </div>
  <div
    v-else-if="showChart"
    class="p-2 h-100"
  >
    <div class="d-flex justify-content-end align-items-center">
      <v-chart-dropdown
        :download-enabled="true"
        :name="$t('word.averages').toLocaleLowerCase()"
      />
    </div>

    <canvas ref="chart" class="chart"></canvas>

    <!-- Invisible chart for downloading -->
    <canvas
      ref="chartDownload"
      class="chart-download"
    ></canvas>
  </div>
  <div v-else class="h-100 d-flex justify-content-center align-items-center">
    <span class="average">
      <v-animated-number
        :value="average.toFixed(1)"
        :formatValue="(value) => (value.toFixed(1))"
        :duration="500"
      />
    </span>
  </div>
</template>

<script>
import { avg } from '@/common/utils/math';
import { COLOR_SCHEMES, COLORS } from '@/common/utils/constants';
import {
  defaultChartOptions,
  defaultDataLabels,
  downloadChart,
} from '@/organisation/services/charts';
import Button from '@/common/components/Button.vue';
import { Chart, registerables } from 'chart.js';
import AnimatedNumber from 'animated-number-vue';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartDropdown from '@/organisation/components/dashboard/ChartDropdown.vue';

export default {
  name: 'ItemDataAverage',
  components: {
    'v-button': Button,
    'v-animated-number': AnimatedNumber,
    'v-chart-dropdown': ChartDropdown,
  },
  props: {
    answers: {
      type: Array,
      required: true,
    },
    totalAverage: {
      type: Number,
      required: false,
    },
    industryAverage: {
      type: Number,
      required: false,
    },
    monthAverage: {
      type: Number,
      required: false,
    },
    previousCommercialsAverage: {
      type: Number,
      required: false,
    },
    otherResearchAverage: {
      type: Number,
      required: false,
    },
    title: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      average: null,
      showChart: false,
      showChartInfo: false,
    };
  },
  computed: {
    colors() {
      const colors = [
        COLORS.red,
      ];
      if (this.otherResearchAverage) {
        colors.push(COLOR_SCHEMES.blue[4]);
      }

      if (this.totalAverage) {
        colors.push(COLOR_SCHEMES.grey[3]);
      }
      if (this.industryAverage) {
        colors.push(COLOR_SCHEMES.grey[4]);
      }
      if (this.previousCommercialsAverage) {
        colors.push(COLOR_SCHEMES.grey[5]);
      }
      if (this.monthAverage) {
        colors.push(COLOR_SCHEMES.grey[6]);
      }
      return colors;
    },
    borderColors() {
      return this.colors.map((color) => `${color}99`);
    },
    dataSets() {
      const data = [
        this.average.toFixed(1),
      ];
      if (this.otherResearchAverage) {
        data.push(this.otherResearchAverage.toFixed(1));
      }
      if (this.totalAverage) {
        data.push(this.totalAverage.toFixed(1));
      }
      if (this.industryAverage) {
        data.push(this.industryAverage.toFixed(1));
      }
      if (this.previousCommercialsAverage) {
        data.push(this.previousCommercialsAverage.toFixed(1));
      }
      if (this.monthAverage) {
        data.push(this.monthAverage.toFixed(1));
      }
      return [
        {
          label: this.$t('word.average'),
          data,
          datalabels: defaultDataLabels({ size: 'md' }),
        },
      ];
    },
    labels() {
      const labels = [
        this.$t('phrase.this_research'),
      ];
      if (this.otherResearchAverage) {
        labels.push(this.$t('phrase.other_research'));
      }
      if (this.totalAverage) {
        labels.push(this.$t('word.platform'));
      }
      if (this.industryAverage) {
        labels.push(this.$t('word.industry'));
      }
      if (this.previousCommercialsAverage) {
        labels.push(this.$t('phrase.previous_commercials'));
      }
      if (this.monthAverage) {
        labels.push(this.$t('word.month'));
      }
      return labels;
    },
  },
  methods: {
    downloadChart,
    async getVerticalLinePlugin() {
      return {
        afterDraw(chart) {
          const context = chart.ctx;
          const firstDataset = chart.config.data.datasets[0];
          const firstValue = firstDataset.data[0];
          const firstColor = firstDataset.backgroundColor[0];
          const xValue = chart.scales.x.getPixelForValue(firstValue);
          context.lineWidth = 3;
          context.beginPath();
          context.moveTo(xValue, chart.options.plugins.title.text ? 50 : 0);
          context.lineTo(xValue, chart.height - 30);
          context.strokeStyle = firstColor;
          context.stroke();
        },
      };
    },
    async createChart() {
      this.showChart = true;
      const linePlugin = await this.getVerticalLinePlugin();
      Chart.register(...registerables);
      const datasets = this.dataSets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: this.colors,
        borderColor: this.borderColors,
        borderWidth: 3,
        datalabels: dataset.datalabels,
      }));

      // eslint-disable-next-line no-new
      this.chart = new Chart(
        this.$refs.chart.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: this.labels,
            datasets,
          },
          plugins: [ChartDataLabels, linePlugin],
          options: {
            ...defaultChartOptions(
              null,
              false,
              false,
              {},
              true,
            ),
            indexAxis: 'y',
            maintainAspectRatio: false,
          },
        },
      );

      // eslint-disable-next-line no-new
      new Chart(
        this.$refs.chartDownload.getContext('2d'),
        {
          type: 'bar',
          data: {
            labels: this.labels,
            datasets,
          },
          plugins: [
            ChartDataLabels,
            linePlugin,
          ],
          options: {
            ...defaultChartOptions(
              this.$t('sentence.averages_for_item', { title: this.title }),
              false,
              false,
              {},
              true,
            ),
            responsive: false,
            indexAxis: 'y',
          },
        },
      );
    },
  },
  created() {
    this.average = avg(this.answers.map((answer) => (answer.value_num)));
    if (this.labels.length > 1) {
      this.createChart();
    }
  },
};
</script>

<style scoped>
span.average span {
  font-size: 100px;
  margin-right: 20px;
  font-weight: lighter;
}

span.average {
  font-size: 100px;
  font-weight: lighter;
}
</style>
